import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text, Badge } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../i18n/I18nProvider';
import { formatDateTime } from '../../lib/format';
import { ConnectMeta } from './ConnectMeta';
import { UssdSection } from './UssdSection';
import { ManyChatSection, ManyChatDrawer, type ManyChatTarget } from './ManyChatSection';
import { HelpButton } from '../../app/guides/HelpButton';
import './channels.css';

/**
 * Channels: where this business can be written to.
 *
 * One page that names all four surfaces and is honest about each. Facebook
 * Messenger and Instagram are connected here. WhatsApp has its own page,
 * because a WhatsApp number is its own product with numbers, templates and a
 * review of its own, and the website widget is issued from the flow builder
 * where the flow it answers with is chosen. Neither is duplicated here: both
 * are pointed at, so somebody who came looking for "add a channel" finds every
 * one of them from a single place rather than learning the map first.
 */
/** Why a connection stopped, by the code the server gives; anything else reads as the general sentence. */
const BROKEN_BODY: Record<string, string> = {
  TOKEN_EXPIRED: 'channels.broken.body.expired',
  NO_PAGE_ACCESS: 'channels.broken.body.access',
  NOT_SUBSCRIBED: 'channels.broken.body.delivery'
};

export default function ChannelsPage() {
  const t = useT();
  const qc = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [checkedId, setCheckedId] = useState<string | null>(null);
  // Facebook and Instagram through ManyChat (23 October 2026): the drawer, for a channel to connect or a setup to show.
  const [manyChat, setManyChat] = useState<ManyChatTarget | null>(null);
  const connectInfo = useQuery({ queryKey: ['meta-connect-info'], queryFn: () => flows.getMetaConnectInfo() });
  const staff = !!connectInfo.data?.data.relay_available;

  const { data, isPending, error } = useQuery({
    queryKey: ['meta-accounts'],
    queryFn: () => flows.listMetaAccounts()
  });
  const accounts = data?.data ?? [];

  const check = useMutation({
    mutationFn: (id: string) => flows.checkMetaAccount(id),
    onSuccess: (res, id) => {
      setCheckedId(id);
      qc.invalidateQueries({ queryKey: ['meta-accounts'] });
      if (res.data.ok) notifySuccess(t('channels.check.ok'), t('nav.channels'));
      else notifyError(t('channels.check.problems'), t('nav.channels'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('nav.channels'))
  });

  const newKey = useMutation({
    mutationFn: (id: string) => flows.newManyChatKey(id),
    onSuccess: (res) => setManyChat({ channel: res.data.account.channel, setup: res.data }),
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('nav.channels'))
  });

  const disconnect = useMutation({
    mutationFn: (id: string) => flows.disconnectMetaAccount(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meta-accounts'] });
      notifySuccess(t('channels.disconnected'), t('nav.channels'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('nav.channels'))
  });

  function confirmDisconnect(id: string, label: string) {
    openConfirmModal({
      title: t('channels.disconnect.title'),
      children: <Text role="bodySmall">{t('channels.disconnect.body', { name: label })}</Text>,
      labels: { confirm: t('channels.disconnect.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => disconnect.mutate(id)
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('channels.title')}
        description={t('channels.description')}
        actions={
          <Group gap="xs">
            <HelpButton guide="account" />
            <Button onClick={() => setConnecting(true)} data-guide="channels-connect">{t('channels.connect')}</Button>
          </Group>
        }
      />

      {error && <Alert color="error" title={t('common.unknownError')}>{String(error)}</Alert>}

      {!isPending && accounts.length === 0 && (
        <EmptyState
          title={t('channels.empty.title')}
          description={t('channels.empty.body')}
          action={<Button onClick={() => setConnecting(true)}>{t('channels.connect')}</Button>}
        />
      )}

      {accounts.map((a) => {
        const checked = checkedId === a.id ? check.data?.data : undefined;
        return (
          <Card withBorder key={a.id} className="rr-channels__card">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Group gap="sm" align="center">
                <span className={`rr-channels__mark rr-channels__mark--${a.channel}`} aria-hidden />
                <Stack gap={2}>
                  <Group gap="xs" align="center">
                    <Text role="body" fw={600}>{a.label}</Text>
                    <Badge size="sm" variant="light">
                      {t(`inbox.channel.${a.channel === 'instagram' ? 'instagram' : 'messenger'}`)}
                    </Badge>
                    {a.via === 'manychat' && <Badge size="sm" variant="outline" data-testid="channel-manychat-badge">{t('channels.manychat.badge')}</Badge>}
                    {!a.is_active && <Badge size="sm" color="gray">{t('channels.switchedOff')}</Badge>}
                    {a.broken_since && <Badge size="sm" color="red" data-testid="channel-broken-badge">{t('channels.broken.badge')}</Badge>}
                  </Group>
                  {/*
                    The one fact that decides whether this works at all. A page
                    that is connected but not delivering looks identical to a
                    healthy one everywhere else on this screen, and its symptom
                    is silence, so it is said here in words rather than left to
                    a check nobody runs.
                  */}
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {a.subscribed_at
                      ? t('channels.receiving', { since: formatDateTime(a.subscribed_at) })
                      : a.via === 'manychat' ? t('channels.manychat.notReceiving') : t('channels.notReceiving')}
                  </Text>
                  {a.last_check_at && (
                    <Text role="caption" c={a.last_check_ok === false ? 'var(--rr-color-danger)' : 'var(--rr-color-text-muted)'}>
                      {a.last_check_ok === false
                        ? t('channels.lastCheck.failed', { when: formatDateTime(a.last_check_at) })
                        : t('channels.lastCheck.ok', { when: formatDateTime(a.last_check_at) })}
                    </Text>
                  )}
                </Stack>
              </Group>

              <Group gap="xs" wrap="nowrap">
                <Button
                  size="xs"
                  variant="default"
                  loading={check.isPending && check.variables === a.id}
                  onClick={() => check.mutate(a.id)}
                >
                  {t('channels.check')}
                </Button>
                {a.via === 'manychat' && staff && (
                  <Button
                    size="xs"
                    variant="default"
                    loading={newKey.isPending && newKey.variables === a.id}
                    onClick={() => newKey.mutate(a.id)}
                  >
                    {t('channels.manychat.newKey')}
                  </Button>
                )}
                <Button size="xs" variant="subtle" color="red" onClick={() => confirmDisconnect(a.id, a.label)}>
                  {t('channels.disconnect')}
                </Button>
              </Group>
            </Group>

            {/*
              The readiness answer, one line per question, failures first.
              Rendered `pre-line` because the point of it is that a person reads
              what is wrong first rather than scrolling past four green ticks
              to find it.
            */}
            {/*
              Facebook ended this Page's connection (16 October 2026): said first, in words, with the one thing that
              fixes it. The business was also told by email and WhatsApp when it happened.
            */}
            {a.broken_since && (
              <Alert className="rr-channels__checks" color="error" title={t('channels.broken.title')} data-testid="channel-broken">
                <Stack gap="xs">
                  <Text role="bodySmall">
                    {a.via === 'manychat'
                      ? t('channels.manychat.broken')
                      : t(BROKEN_BODY[a.broken_code ?? ''] ?? 'channels.broken.body.other')}
                  </Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('channels.broken.since', { when: formatDateTime(a.broken_since) })}</Text>
                  <Group>
                    <Button
                      size="xs"
                      color="red"
                      onClick={() => (a.via === 'manychat' ? setManyChat({ channel: a.channel }) : setConnecting(true))}
                      data-testid="channel-reconnect"
                    >
                      {t('channels.broken.action')}
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            )}

            {checked && (
              <Alert
                className="rr-channels__checks"
                color={checked.ok ? 'success' : 'error'}
                title={checked.ok ? t('channels.check.ok') : t('channels.check.problems')}
              >
                <span style={{ whiteSpace: 'pre-line' }}>{checked.summary}</span>
              </Alert>
            )}
          </Card>
        );
      })}

      {/* Through ManyChat until Meta approves (23 October 2026): only platform staff acting for the business see this. */}
      <ManyChatSection onConnect={(channel) => setManyChat({ channel })} />

      {/* USSD codes (4 October 2026): applied for and listed here; shown only when USSD is open or the business has one. */}
      <UssdSection />

      {/* The other two surfaces: named, pointed at, never duplicated. */}
      <Card withBorder className="rr-channels__elsewhere" data-guide="channels-other">
        <Stack gap="xs">
          <Text role="bodySmall" fw={600}>{t('channels.elsewhere.title')}</Text>
          <Text role="bodySmall">
            {t('channels.elsewhere.whatsapp')} <Link to="/numbers">{t('nav.numbers')}</Link>
          </Text>
          <Text role="bodySmall">
            {t('channels.elsewhere.website')} <Link to="/flows">{t('nav.flows')}</Link>
          </Text>
        </Stack>
      </Card>

      <Drawer
        opened={connecting}
        onClose={() => setConnecting(false)}
        title={t('channels.connect')}
        position="right"
        size="lg"
      >
        <ConnectMeta
          connected={accounts}
          onDone={() => {
            setConnecting(false);
            qc.invalidateQueries({ queryKey: ['meta-accounts'] });
          }}
        />
      </Drawer>

      <ManyChatDrawer
        target={manyChat}
        onClose={() => setManyChat(null)}
        onDone={() => qc.invalidateQueries({ queryKey: ['meta-accounts'] })}
      />
    </Stack>
  );
}
